/** Partner Access (Settings) — port of v1 scraper-health.js partnersPanel.
 *
 * Gives a partner a private, read-only link (/partner.html?t=…) that surfaces
 * ONLY the deals you pick — nothing else in the CRM is reachable. Mirrors v1's
 * partnerNew / partnerToggleDeal / partnerRevoke / partnerLogPayment, but the
 * store is the PARTNERS settings blob (an array) persisted wholesale through
 * the settings door (opPutPartners), just like the contractors/expenses
 * ledgers. Editing is gated by `canEdit` (liveFire settingsEdit) exactly like
 * the other settings tabs; a read-only viewer still sees each link + shares. */
import { useRef, useState } from "react";
import { moneyExact } from "../../lib/money";
import { narrowPartners, SETTINGS_MSG, type Partner, type PartnerPayment } from "./api";
import { useConfirmDelete } from "../common/ConfirmDelete";

/** The public partner viewer (partner.html, now shipped in v2's public/ so it's
 * served on the CURRENT domain). 2026-07-23: was hardcoded to the old
 * reicrm.pages.dev domain; use location.origin so the link matches wherever v2
 * runs (etopropertygroupcrm.com). SSR-safe fallback to the live domain. */
const partnerBase = (): string => {
  try {
    if (typeof window !== "undefined" && window.location?.origin) return window.location.origin;
  } catch { /* SSR / sandbox */ }
  return "https://etopropertygroupcrm.com";
};
const partnerLink = (token: string): string =>
  `${partnerBase()}/partner.html?t=${encodeURIComponent(token)}`;

// id + token from Date.now() + an in-session counter — collision-free at runtime
// (no crypto import needed; the token is the link secret, so it stays opaque).
let _seq = 0;
const freshId = (): string => `p${Date.now().toString(36)}${(_seq++).toString(36)}`;
const freshToken = (): string =>
  `${Date.now().toString(36)}${(_seq++).toString(36)}${(_seq++).toString(36)}`;

export function PartnerAccessTab({ raw, deals, canEdit, onSave }: {
  raw: unknown;
  deals: { key: string; label: string }[];
  canEdit: boolean;
  onSave: (all: Partner[]) => void;
}) {
  const partners = narrowPartners(raw);
  const [name, setName] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);
  const confirmDel = useConfirmDelete(); // v1 partnerDelete — typed-delete gated

  // Every mutation rewrites the WHOLE array through the door (v1 blob semantics).
  const commit = (next: Partner[]) => { if (canEdit) onSave(next); };
  const patch = (id: string, fn: (p: Partner) => Partner) =>
    commit(partners.map((p) => (p.id === id ? fn(p) : p)));

  const addPartner = () => {
    const nm = name.trim();
    if (!canEdit) return;
    if (!nm) { inputRef.current?.focus(); return; } // empty → focus the field instead of a dead click
    commit([...partners, { id: freshId(), name: nm, token: freshToken(), deals: [], payments: [] }]);
    setName("");
  };
  const removePartner = (id: string) => commit(partners.filter((p) => p.id !== id));
  const toggleDeal = (id: string, key: string) =>
    patch(id, (p) => ({
      ...p,
      deals: p.deals.includes(key) ? p.deals.filter((k) => k !== key) : [...p.deals, key],
    }));
  const logPayment = (id: string, pay: PartnerPayment) =>
    patch(id, (p) => ({ ...p, payments: [pay, ...p.payments] }));
  const editPayment = (id: string, i: number, pay: PartnerPayment) =>
    patch(id, (p) => ({ ...p, payments: p.payments.map((x, xi) => (xi === i ? pay : x)) }));
  const deletePayment = (id: string, i: number) => {
    const pm = partners.find((x) => x.id === id)?.payments[i];
    confirmDel.ask(`Delete this ${moneyExact(pm?.amount ?? 0)} payment log?`, () =>
      patch(id, (q) => ({ ...q, payments: q.payments.filter((_, xi) => xi !== i) })));
  };

  return (
    <div className="setsec">
      <div className="setuserhd">
        <h3>Partner Access</h3>
      </div>
      <p className="sub setnote">Give a partner a private link that shows only the deals you choose — read-only. They never see anything else in the CRM, and you can revoke it anytime.</p>

      {!canEdit ? (
        <div className="setgate">{SETTINGS_MSG.editPhaseB} — your current partners are shown below.</div>
      ) : (
        <div className="setpartnernew">
          <input
            ref={inputRef}
            className="setpartnernewinput"
            value={name}
            placeholder="Partner name — e.g. Dana (money partner)"
            onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") addPartner(); }}
          />
          <button className="btn slim primary" onClick={addPartner}>+ New partner</button>
        </div>
      )}

      {partners.length === 0 ? (
        <div className="setpartnerempty">No partners yet. Create one to hand out a private, revocable link.</div>
      ) : (
        <div className="setpartnerlist">
          {partners.map((p) => (
            <PartnerCard
              key={p.id}
              partner={p}
              deals={deals}
              canEdit={canEdit}
              onToggleDeal={(key) => toggleDeal(p.id, key)}
              onLogPayment={(pay) => logPayment(p.id, pay)}
              onEditPayment={(i, pay) => editPayment(p.id, i, pay)}
              onDeletePayment={(i) => deletePayment(p.id, i)}
              onRemove={() => confirmDel.ask("Delete this partner and their access link permanently?", () => removePartner(p.id))}
            />
          ))}
        </div>
      )}

      <div className="setpartnerexplain">
        <strong>What partners see:</strong> only the deals you share above — each one's stage, estimated money needed,
        purchasing, inspection &amp; timeline, rehab details, expenses, property photos, and any payments you log. It's
        read-only, sensitive fields (lockbox, scope-of-work, company docs) are stripped, and nothing else in your CRM is reachable.
      </div>
      {confirmDel.modal}
    </div>
  );
}

function PartnerCard({ partner, deals, canEdit, onToggleDeal, onLogPayment, onEditPayment, onDeletePayment, onRemove }: {
  partner: Partner;
  deals: { key: string; label: string }[];
  canEdit: boolean;
  onToggleDeal: (key: string) => void;
  onLogPayment: (pay: PartnerPayment) => void;
  onEditPayment: (i: number, pay: PartnerPayment) => void;
  onDeletePayment: (i: number) => void;
  onRemove: () => void;
}) {
  const link = partnerLink(partner.token);
  const [copied, setCopied] = useState(false);
  const [amount, setAmount] = useState("");
  const [note, setNote] = useState("");
  const [dealSel, setDealSel] = useState(""); // which property the payment is for
  // inline edit of one logged payment (index into partner.payments)
  const [editIdx, setEditIdx] = useState<number | null>(null);
  const [eAmt, setEAmt] = useState("");
  const [eNote, setENote] = useState("");
  const [eDeal, setEDeal] = useState("");
  const sharedDeals = deals.filter((d) => partner.deals.includes(d.key));
  const dealLabel = (k?: string): string => (k ? (deals.find((d) => d.key === k)?.label ?? k) : "");
  const startEdit = (i: number) => {
    const pm = partner.payments[i];
    if (!pm) return;
    setEditIdx(i); setEAmt(String(pm.amount)); setENote(pm.note); setEDeal(pm.deal ?? "");
  };
  const saveEdit = () => {
    if (editIdx === null) return;
    const amt = Number(eAmt.replace(/[^0-9.]/g, ""));
    const prev = partner.payments[editIdx];
    if (!prev || !Number.isFinite(amt) || amt <= 0) return;
    const { deal: _d, ...rest } = prev;
    onEditPayment(editIdx, { ...rest, amount: amt, note: eNote.trim(), ...(eDeal ? { deal: eDeal } : {}) });
    setEditIdx(null);
  };

  const copy = () => {
    try {
      // writeText throws synchronously if clipboard is absent (non-secure ctx);
      // the rejection handler covers a permissions block. Link stays selectable.
      void navigator.clipboard.writeText(link).then(
        () => { setCopied(true); setTimeout(() => setCopied(false), 1600); },
        () => { /* blocked — the user can still select the visible link */ },
      );
    } catch { /* no clipboard API in this context */ }
  };

  const submitPayment = () => {
    if (!canEdit) return;
    const amt = Number(amount.replace(/[^0-9.]/g, ""));
    if (!Number.isFinite(amt) || amt <= 0) return;
    onLogPayment({
      amount: amt,
      note: note.trim(),
      date: new Date().toLocaleDateString("en-US", { month: "short", day: "numeric", year: "2-digit" }),
      ...(dealSel ? { deal: dealSel } : {}),
    });
    setAmount("");
    setNote("");
    setDealSel("");
  };

  const shared = partner.deals.length;
  const paid = partner.payments.length;

  return (
    <div className="setpartnercard">
      <div className="setpartnerhd">
        <strong>{partner.name || "Partner"}</strong>
        {canEdit ? (
          <button className="setpartnerdel" title="Delete this partner and disable their link" onClick={onRemove}>Revoke</button>
        ) : null}
      </div>
      <div className="setpartnermeta">{shared} deal{shared === 1 ? "" : "s"} shared · {paid} payment{paid === 1 ? "" : "s"} logged</div>

      <div className="setpartnerlink">
        <input className="setpartnerlinkinput" readOnly value={link} onFocus={(e) => e.currentTarget.select()} />
        <button className="btn slim" onClick={copy}>{copied ? "Copied ✓" : "Copy link"}</button>
      </div>

      <div className="setpartnerchips">
        {deals.length === 0 ? (
          <span className="setpartnermeta">No deals yet to share.</span>
        ) : deals.map((d) => {
          const on = partner.deals.includes(d.key);
          return (
            <button
              key={d.key}
              className={"setpartnerchip" + (on ? " on" : "")}
              disabled={!canEdit}
              aria-pressed={on}
              onClick={() => onToggleDeal(d.key)}
            >
              {d.label}{on ? " ✓" : ""}
            </button>
          );
        })}
      </div>

      {canEdit ? (
        <div className="setpartnerpay">
          <span className="setpartnerpaytitle">Log a payment they made</span>
          <select className="setpartnerpaydeal" value={dealSel} onChange={(e) => setDealSel(e.target.value)} title="Which property is this payment for?">
            <option value="">— Which property? (general) —</option>
            {sharedDeals.map((d) => <option key={d.key} value={d.key}>{d.label}</option>)}
          </select>
          <input
            className="setpartnerpayamt"
            inputMode="decimal"
            value={amount}
            placeholder="Amount ($)"
            onChange={(e) => setAmount(e.target.value)}
          />
          <input
            className="setpartnerpaynote"
            value={note}
            placeholder="Note (optional) — e.g. earnest money"
            onChange={(e) => setNote(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") submitPayment(); }}
          />
          <button className="btn slim" disabled={!amount.trim()} onClick={submitPayment}>Log payment</button>
        </div>
      ) : null}

      {paid ? (
        <ul className="setpartnerpaylist">
          {partner.payments.map((pm, i) => (editIdx === i ? (
            <li key={i} className="setpartnerpayedit">
              <input className="setpartnerpayamt" inputMode="decimal" value={eAmt} onChange={(e) => setEAmt(e.target.value)} />
              <input className="setpartnerpaynote" value={eNote} placeholder="Note" onChange={(e) => setENote(e.target.value)} />
              <select className="setpartnerpaydeal" value={eDeal} onChange={(e) => setEDeal(e.target.value)}>
                <option value="">— General (no property) —</option>
                {sharedDeals.map((d) => <option key={d.key} value={d.key}>{d.label}</option>)}
              </select>
              <button className="btn slim primary" onClick={saveEdit}>Save</button>
              <button className="btn slim" onClick={() => setEditIdx(null)}>Cancel</button>
            </li>
          ) : (
            <li key={i}>
              <span>
                {moneyExact(pm.amount)}
                {pm.note ? ` · ${pm.note}` : ""}
                {pm.deal ? ` · 🏠 ${(dealLabel(pm.deal).split(",")[0] ?? pm.deal)}` : ""}
                {" · "}{pm.date}
              </span>
              {canEdit ? (
                <span className="setpartnerpayacts">
                  <button className="rowbtn" title="Edit this payment" onClick={() => startEdit(i)}>✎</button>
                  <button className="rowbtn del" title="Delete this payment" onClick={() => onDeletePayment(i)}>×</button>
                </span>
              ) : null}
            </li>
          )))}
        </ul>
      ) : null}
    </div>
  );
}
